import { useAsyncCallback } from "../../hooks/useAsyncCallback";
import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import type {
  AutoReplyDraftResponse,
  LeadScoreBand,
} from "@io-spoke/contracts/leadScoring";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { Textarea } from "@components/ui/textarea";
import { createAutoReplyDraft, sendAutoReply } from "../../lib/leads";

type AutoReplyComposerProps = {
  leadId: string;
  band?: LeadScoreBand;
  intentSummary?: string;
};

const draftFormSchema = z.object({
  recipientName: z.string().trim().optional(),
  productName: z.string().trim().optional(),
  callToAction: z.string().trim().optional(),
});
type DraftFormValues = z.infer<typeof draftFormSchema>;

const sendFormSchema = z.object({
  recipientEmail: z.string().trim().min(1, "Recipient email is required to send.").email("Enter a valid recipient email."),
  subject: z.string().trim().min(1, "Subject is required to send."),
  body: z.string().trim().min(1, "Body is required to send."),
});
type SendFormValues = z.infer<typeof sendFormSchema>;

export function AutoReplyComposer({ leadId, band, intentSummary }: AutoReplyComposerProps) {
  const [draft, setDraft] = useState<AutoReplyDraftResponse | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<string | null>(null);

  const draftForm = useForm<DraftFormValues>({
    resolver: zodResolver(draftFormSchema),
    defaultValues: { recipientName: "", productName: "IO Spoke", callToAction: "" },
  });
  const sendForm = useForm<SendFormValues>({
    resolver: zodResolver(sendFormSchema),
    defaultValues: { recipientEmail: "", subject: "", body: "" },
  });

  const canDraft = Boolean(band) && leadId.trim().length > 0;

  const placeholderHint = useMemo(() => {
    if (!draft || draft.placeholders.length === 0) {
      return null;
    }
    return `Placeholders: ${draft.placeholders.join(", ")}`;
  }, [draft]);

  const {
    pending: generateDraftPending,
    callback: generateDraft,
  } = useAsyncCallback(async (values: DraftFormValues) => {
    if (!band) {
      setDraftError("Score the lead before drafting a reply.");
      return;
    }
    setIsConfirming(false);
    setDraftError(null);
    setSendError(null);
    setSentAt(null);
    try {
      const result = await createAutoReplyDraft({
        leadId,
        band,
        intentSummary,
        recipientName: values.recipientName || undefined,
        productName: values.productName || undefined,
        callToAction: values.callToAction || undefined,
      });
      setDraft(result);
      sendForm.reset({ recipientEmail: sendForm.getValues("recipientEmail"), subject: result.subject, body: result.body });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Draft generation failed.";
      setDraftError(message);
      setDraft(null);
    }
  });

  const reviewSend = () => {
    if (!draft) {
      setSendError("Generate a draft before reviewing.");
      return;
    }
    setIsConfirming(true);
    setSendError(null);
  };

  const {
    pending: confirmSendPending,
    callback: confirmSend,
  } = useAsyncCallback(async (values: SendFormValues) => {
    if (!draft) {
      setSendError("Generate a draft before sending.");
      return;
    }
    setSendError(null);
    setSentAt(null);
    try {
      const result = await sendAutoReply({
        leadId,
        draftId: draft.draftId,
        recipientEmail: values.recipientEmail,
        subject: values.subject,
        body: values.body,
      });
      if (result.status === "sent") {
        const timestamp = result.sentAt ?? new Date().toISOString();
        setSentAt(timestamp);
        toast.success(result.message ?? "Reply sent.");
      } else {
        const message = result.message ?? "Reply failed to send.";
        setSendError(message);
        toast.error(message);
      }
      setIsConfirming(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Send failed.";
      setSendError(message);
      toast.error(message);
      setIsConfirming(false);
    }
  });

  const subject = sendForm.watch("subject");
  const body = sendForm.watch("body");

  return (
    <Card className="border-border/70">
      <CardHeader>
        <CardTitle className="text-base">Auto-reply draft</CardTitle>
        <CardDescription>
          Generate a tailored response, edit it, then confirm before sending.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form className="grid gap-3 md:grid-cols-3" onSubmit={draftForm.handleSubmit(generateDraft)} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="lead-recipient">Recipient</Label>
            <Input id="lead-recipient" placeholder="Jordan" {...draftForm.register("recipientName")} disabled={draftForm.formState.isSubmitting} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-product">Product</Label>
            <Input id="lead-product" {...draftForm.register("productName")} disabled={draftForm.formState.isSubmitting} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-cta">Call to action</Label>
            <Input id="lead-cta" placeholder="Propose a 15-minute call" {...draftForm.register("callToAction")} disabled={draftForm.formState.isSubmitting} />
          </div>
          <div className="flex flex-wrap items-center gap-2 md:col-span-3">
            <Button loading={generateDraftPending} loadingLabel="Generating draft…" type="submit" disabled={!canDraft || draftForm.formState.isSubmitting}>
              {draftForm.formState.isSubmitting ? "Generating…" : "Generate draft"}
            </Button>
            {!band ? (
              <div className="text-xs text-muted-foreground">Score the lead to unlock drafting.</div>
            ) : null}
          </div>
        </form>

        {draftError ? <div className="text-sm text-destructive" role="alert">{draftError}</div> : null}

        {draft ? (
          <form className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3" onSubmit={sendForm.handleSubmit(confirmSend)} noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="lead-recipient-email">Recipient email</Label>
              <Input
                id="lead-recipient-email"
                type="email"
                placeholder="jordan@acme.com"
                {...sendForm.register("recipientEmail")}
                disabled={sendForm.formState.isSubmitting}
                aria-invalid={Boolean(sendForm.formState.errors.recipientEmail)}
                aria-describedby={sendForm.formState.errors.recipientEmail ? "lead-recipient-email-error" : undefined}
              />
              {sendForm.formState.errors.recipientEmail ? <p id="lead-recipient-email-error" role="alert" className="text-xs text-destructive">{sendForm.formState.errors.recipientEmail.message}</p> : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-draft-subject">Subject</Label>
              <Input
                id="lead-draft-subject"
                {...sendForm.register("subject")}
                disabled={sendForm.formState.isSubmitting}
                aria-invalid={Boolean(sendForm.formState.errors.subject)}
                aria-describedby={sendForm.formState.errors.subject ? "lead-draft-subject-error" : undefined}
              />
              {sendForm.formState.errors.subject ? <p id="lead-draft-subject-error" role="alert" className="text-xs text-destructive">{sendForm.formState.errors.subject.message}</p> : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-draft-body">Body</Label>
              <Textarea
                id="lead-draft-body"
                className="min-h-[160px]"
                {...sendForm.register("body")}
                disabled={sendForm.formState.isSubmitting}
                aria-invalid={Boolean(sendForm.formState.errors.body)}
                aria-describedby={sendForm.formState.errors.body ? "lead-draft-body-error" : undefined}
              />
              {sendForm.formState.errors.body ? <p id="lead-draft-body-error" role="alert" className="text-xs text-destructive">{sendForm.formState.errors.body.message}</p> : null}
            </div>
            {placeholderHint ? <div className="text-xs text-muted-foreground">{placeholderHint}</div> : null}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={reviewSend} disabled={draftForm.formState.isSubmitting}>Review send</Button>
            </div>

            {sendError ? <div className="text-sm text-destructive" role="alert">{sendError}</div> : null}

            {isConfirming ? (
              <div className="space-y-3 rounded-lg border border-border bg-background p-4">
                <div className="space-y-1">
                  <div className="text-sm font-semibold">Confirm send</div>
                  <p className="text-xs text-muted-foreground">This is a confirmation step. Sending is not automatic.</p>
                </div>
                <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
                  <div className="font-medium text-foreground">Subject</div>
                  <div className="mt-1">{subject}</div>
                  <div className="mt-3 font-medium text-foreground">Body</div>
                  <pre className="mt-1 whitespace-pre-wrap font-sans text-xs">{body}</pre>
                </div>
                <Button loading={confirmSendPending} loadingLabel="Sending reply…" type="submit" disabled={sendForm.formState.isSubmitting}>
                  {sendForm.formState.isSubmitting ? "Sending…" : "Send now"}
                </Button>
              </div>
            ) : null}
          </form>
        ) : null}

        {sentAt ? (
          <div className="text-xs text-muted-foreground">
            Reply marked sent at {new Date(sentAt).toLocaleString()}.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
